import React from 'react';
import styled from 'styled-components';

import useTranslation from 'hooks/use-translation';
import { StarRating } from 'components/reviews/star-rating';
import { EffectChip } from 'components/form-inputs/effects-chips-input/effect-chip';
import { RatingDistribution } from './rating-distribution';
import { topEffects } from './review-utils';
import type { ProductRatingSummary } from './use-product-rating-summary';

type ReviewSummaryProps = {
  summary: ProductRatingSummary;
};

export const ReviewSummary = ({ summary }: ReviewSummaryProps): JSX.Element | null => {
  const { t } = useTranslation();
  const effects = topEffects(summary.effectsCounts);

  if (!summary.aiSummary) {
    return null;
  }

  return (
    <Container data-testid='review-summary'>
      <ScoreRow>
        <Score>
          {summary.avgRating != null && (
            <>
              <ScoreValue>
                {t('product.reviews.score', '{{rating}} out of 5', { rating: summary.avgRating.toFixed(1) })}
              </ScoreValue>
              <StarRating value={summary.avgRating} size={20} />
            </>
          )}
          <ReviewCount>{t('product.reviews.count', '{{count}} Reviews', { count: summary.reviewCount })}</ReviewCount>
        </Score>
        <RatingDistribution summary={summary} />
      </ScoreRow>

      <Block>
        <BlockTitle>{t('product.reviews.customers-say', 'Customers say')}</BlockTitle>
        <SummaryText>{summary.aiSummary}</SummaryText>
      </Block>

      {effects.length > 0 && (
        <Block>
          <BlockTitle>{t('product.reviews.top-effects', 'Top effects mentioned')}</BlockTitle>
          <Effects>
            {effects.map(({ effect, count }) => (
              <EffectChip key={effect} effect={effect} count={count} variant='outlined' />
            ))}
          </Effects>
        </Block>
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.grey[95]};
`;

const ScoreRow = styled.div`
  display: flex;
  gap: 32px;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    flex-direction: column;
    gap: 20px;
  }
`;

const Score = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 120px;
`;

const ScoreValue = styled.span`
  font-size: 22px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.grey[30]};
`;

const ReviewCount = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[45]};
`;

const Block = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const BlockTitle = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.grey[30]};
`;

const SummaryText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.grey[30]};
`;

const Effects = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
